import { FC, HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import TBreadcrumbs from "@/components/atoms/breadcrumbs/TBreadcrumbs";
import HtmlText from "./HtmlText";
import { format } from "date-fns";
import Image from "next/image";

type IBannerSectionProps = HTMLAttributes<HTMLDivElement> & {
  imageClasses?: string;
  headingClasses?: string;
  subHeadingClasses?: string;
  descriptionClasses?: string;
  breadcrumbs?: { title?: string; href?: string }[];

  image?: string;
  heading?: string;
  subHeading?: string;
  description?: ReactNode;
  author?: {
    avatar?: string;
    name?: string;
    date?: string;
    time?: string;
  };
};

const BannerSection: FC<IBannerSectionProps> = ({
  imageClasses,
  headingClasses,
  subHeadingClasses,
  descriptionClasses,
  breadcrumbs,
  image,
  heading,
  subHeading,
  description,
  author,
  className,
  ...other
}) => {
  return (
    <section>
      <div
        className={clsx(
          "relative h-full w-full overflow-hidden",
          className,
        )}
        {...other}
      >
        {/* Banner background — rendered as priority <Image> instead of a
            CSS background-image. Browser preloaders can't discover CSS
            background images, so the hero used to load AFTER CSS parse,
            inflating LCP by ~1s. With priority + fill, Next.js emits a
            <link rel=preload as=image> in <head> automatically, making
            the banner the LCP candidate the browser sees first. */}
        {image && (
          <Image
            src={image}
            alt=""
            fill
            priority
            sizes="100vw"
            className={clsx("object-cover", imageClasses)}
          />
        )}
        <div
          className={clsx(
            "relative",
            !!image ? "bg-black-950/65" : "bg-primary-900",
          )}
        >
          <div className="container py-10 md:py-14 lg:py-20 xl:py-24">
            {breadcrumbs?.length && <TBreadcrumbs breadcrumbs={breadcrumbs} />}

            {/* Canonical hero heading scale matching the site's redesigned
                sections — Bricolage Grotesque, fluid clamp from 26px on
                phones to 64px on desktop. Sub-heading (smaller line above
                the main title) uses the SectionHeader subtitle clamp. */}
            {heading && (
              <h1 className="font-bricolage-grotesque font-medium !leading-[1.1]">
                {subHeading && (
                  <span
                    className={clsx(
                      "mb-2 block text-[clamp(1rem,0.75vw+0.75rem,1.5rem)] font-normal text-white/85 md:mb-3",
                      subHeadingClasses,
                    )}
                  >
                    {subHeading}
                  </span>
                )}

                <span
                  className={clsx(
                    "mb-4 block text-[clamp(1.625rem,3.5vw+0.75rem,4rem)] text-yellow-400 md:mb-5",
                    headingClasses,
                  )}
                >
                  {heading}
                </span>
              </h1>
            )}

            {description && (
              <HtmlText
                className={clsx(
                  // Responsive width: full on mobile, 2/3 on desktop —
                  // keeps the description from line-wrapping awkwardly
                  // on narrow viewports.
                  "w-full lg:w-2/3",
                  // Canonical description scale + responsive weight.
                  "prose-p:!text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] prose-p:!font-light prose-p:!leading-relaxed prose-p:!text-white/85 lg:prose-p:!font-extralight",
                  "prose-headings:text-white prose-strong:text-white",
                  descriptionClasses,
                )}
              >
                {description}
              </HtmlText>
            )}

            {author && (
              // Editorial byline — matches the BlogCard footer pattern:
              // mono uppercase tracked meta, "BY NAME · DATE · X MIN",
              // smaller avatar. Reads as one rhythm with the rest of
              // the editorial system. Wraps cleanly on narrow viewports.
              <div className="mt-6 flex flex-wrap items-center gap-3 text-white/90 md:mt-8 md:gap-4">
                {author.avatar && (
                  <Image
                    className="h-9 w-9 rounded-full ring-1 ring-white/30 object-cover"
                    src={author.avatar}
                    alt={author?.name ?? ""}
                    height={36}
                    width={36}
                    unoptimized
                  />
                )}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] font-normal uppercase tracking-[0.08em] text-white/85 md:text-xs">
                  {author.name && (
                    <span>
                      By <span className="text-white">{author.name}</span>
                    </span>
                  )}
                  {author.name && author.date && <span aria-hidden="true">•</span>}
                  {author.date && (
                    <span>{format(author.date, "MMM d, yyyy")}</span>
                  )}
                  {author.date && author.time && <span aria-hidden="true">•</span>}
                  {author.time && <span>{author.time} Min Read</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BannerSection;
